import React, { useEffect, useRef, useState } from "react"
import BigCalendar from "../../../../components/compounds/bigCalendar/BigCalendar.compound"
import { DateTime } from "luxon"
import useScreenDimensionsContext from "../../../../hooks/contextConsumers/useScreenDimensionsContext.hook"
import { formatVacationEventsTemplates } from "../../../../utils/bigCalendar.utils"
import Card from "../../../../components/atoms/Card.atom"
import { MY_VACATION_STATES } from "../../../../consts/modules/vacations.consts"
import {
  BIG_CALENDAR_VIEWS,
  DEFAULT_BIG_CALENDAR_MESSAGES,
} from "../../../../consts/bigCalendar.consts"
import useFetchTeamMemberVacationEvents from "../../../../hooks/useFetchTeamMemberVacationEvents.hook"
import {
  generatePeriodEndEvent,
  generatePeriodStartEvent,
  getTeamMemberVacationsCalendarBounds,
  isEventHolidayOfAnyType,
  isEventPeriodDelimiter,
} from "../../../../utils/vacation.utils"
import MyVacationsGroupedEventsOverlayPanel from "../../components/MyVacationsGroupedEventsOverlayPanel.compound"

const CalendarTeamMemberVacationRequests = ({
  refresh,
  selectedPeriod = [],
  setSelectedPeriod = () => {},
  setVisibleModal,
  setSelectedVacationRequest,
  workAnniversary = undefined
}) => {
  const isInternalNavigation = useRef(false)
  const [vacationRequests, setVacationRequests] = useState([])
  const bounds = getTeamMemberVacationsCalendarBounds(workAnniversary)
  const [
    moreVacationRequestsPanelContent,
    setMoreVacationRequestsPanelContent,
  ] = useState([])
  const [groupedEvents, setGroupedEvents] = useState([])
  const [currentView, setCurrentView] = useState(BIG_CALENDAR_VIEWS.MONTH)
  const getInitialDate = (selectedPeriod) => {
    if (!selectedPeriod || selectedPeriod.length !== 2) return new Date()
    const today = DateTime.local()
    const [periodStart, periodEnd] = selectedPeriod
    const isTodayInPeriod =
      today >= periodStart.startOf("day") && today <= periodEnd.endOf("day")
    return (isTodayInPeriod ? today : periodStart).toJSDate()
  }
  const [currentDate, setCurrentDate] = useState(() =>
    getInitialDate(selectedPeriod)
  )
  const [clickedDay, setClickedDay] = useState(null)
  const [loading, setLoading] = useState(false)
  const { screenWidth } = useScreenDimensionsContext()
  const { fetchVacationRequests, fetchHolidays } =
    useFetchTeamMemberVacationEvents({
      selectedPeriod,
    })
  const opRef = useRef(null)

 useEffect(() => {
    fetchData()
    
    if (isInternalNavigation.current) {
      isInternalNavigation.current = false
    } else {
      setCurrentDate(getInitialDate(selectedPeriod))
    }
  }, [selectedPeriod, refresh])

  const fetchData = async () => {
    setLoading(true)
    setVacationRequests([])
    try {
      const vacationRequestsResponse = await fetchVacationRequests()
      const holidaysResponse = await fetchHolidays()
      setVacationRequests([
        generatePeriodEndEvent(selectedPeriod[0].minus({ days: 1 })),
        generatePeriodStartEvent(selectedPeriod[0]),
        ...vacationRequestsResponse,
        ...holidaysResponse,
        generatePeriodEndEvent(selectedPeriod[1]),
        generatePeriodStartEvent(selectedPeriod[1].plus({ days: 1 })),
      ])
    } catch (error) {
      console.error("Error fetching vacation requests:", error)
      setVacationRequests([])
    } finally {
      setLoading(false)
    }
  }

  const handleNavigate = (date, view, action) => {
    const targetDate = DateTime.fromJSDate(date)
    const [periodStart, periodEnd] = selectedPeriod
    const isAfterCurrentPeriod = targetDate > periodEnd.endOf('day')
    const isBeforeCurrentPeriod = targetDate < periodStart.startOf('day')

    if (isAfterCurrentPeriod || isBeforeCurrentPeriod) {
      isInternalNavigation.current = true 
      
      if (isAfterCurrentPeriod) {
        setSelectedPeriod([
          periodStart.plus({ years: 1 }),
          periodEnd.plus({ years: 1 })
        ])
      } else {
        setSelectedPeriod([
          periodStart.minus({ years: 1 }),
          periodEnd.minus({ years: 1 })
        ])
      }
    }
    opRef?.current?.hide()
    setCurrentDate(date)
  }

  const eventPropGetter = (event) => {
    const eventStart = DateTime.fromJSDate(event.start)
    const visibleMonth = DateTime.fromJSDate(currentDate).month
    const isEventOutsideVisibleMonth =
      currentView === BIG_CALENDAR_VIEWS.MONTH &&
      eventStart.month !== visibleMonth
    if (isEventOutsideVisibleMonth) {
      return {
        style: {
          opacity: 0.5,
        },
      }
    }
    if ("groupedEvents" in event) {
      return {
        className: "grouped-event",
      }
    }
    return {}
  }
  const handleEventClick = (
    event,
    e,
    options = {
      groupedEvents: true,
    }
  ) => {
    if (loading) return
    if (isEventHolidayOfAnyType(event)) return
    if (isEventPeriodDelimiter(event)) {
      return
    }
    const selectedEvents =
      currentView === BIG_CALENDAR_VIEWS.MONTH && options.groupedEvents ?
        groupedEvents
      : vacationRequests

    if (!selectedEvents[event.id]?.groupedEvents) {
      setSelectedVacationRequest({
        ...event,
        id: event?.idVacations || event.id,
      })
      setVisibleModal(MY_VACATION_STATES.QUERY)
      opRef?.current?.hide(e)
      return
    }
    const element = document.querySelector(".hovered-cell")
    if (element) {
      setClickedDay(
        DateTime.fromISO(element.classList[1]).startOf("day").toJSDate()
      )
      setTimeout(() => {
        const newTarget = document.querySelector(
          "." +
            Array.from(e.target.classList).join(".") +
            '[title="' +
            e.target.title +
            '"]'
        )
        if (newTarget && newTarget !== e.target) {
          e.target = newTarget
        }
      }, 0)
    }
    opRef?.current?.hide(e)
    setTimeout(() => {
      opRef?.current?.show(e)
    }, 0)
  }

  return (
    <Card
      className="calendar-card-container shadow-2"
      style={{ height: screenWidth > 860 ? "90vh" : "72vh", padding: "1rem" }}
    >
      <BigCalendar
        key={currentDate.toISOString()}
        loading={loading}
        events={vacationRequests}
        onView={(e) => {
          opRef?.current?.hide(e)
          setCurrentView(e)
        }}
        views={{
          week: screenWidth > 728,
          month: true,
          placeholder: true,
          // year: screenWidth > 728 ? YearView : false,
        }}
        view={screenWidth <= 728 ? BIG_CALENDAR_VIEWS.MONTH : currentView}
        popup
        style={{ height: "100%" }}
        eventPropGetter={eventPropGetter}
        handleNavigate={handleNavigate}
        formatEventsTemplates={formatVacationEventsTemplates}
        handleEventClick={handleEventClick}
        setGroupedEvents={setGroupedEvents}
        tooltipAccessor={(e) => e.prevTitle}
        date={currentDate}
        min={bounds.lastYearPeriod.start}
        max={bounds.nextYearPeriod.end}
        groupEvents={false}
        messages={{
          ...DEFAULT_BIG_CALENDAR_MESSAGES,
          showMore: (total, remainingEvents, events) => {
            return (
              <div
                className="w-full show-more-default-button-content-cell gap-1 flex"
                title={`${total} más`}
                onClick={(e) => {
                  e.stopPropagation()
                  opRef.current?.show(e)
                  setMoreVacationRequestsPanelContent(remainingEvents)
                  setClickedDay(remainingEvents[0].start)
                }}
              >
                <i className="pi pi-ban"></i>
                <span className="font-mulish">{total} más</span>
              </div>
            )
          },
        }}
      />
      <MyVacationsGroupedEventsOverlayPanel
        opRef={opRef}
        vacationRequests={moreVacationRequestsPanelContent}
        clickedDay={clickedDay}
        handleEventClick={handleEventClick}
      />
    </Card>
  )
}

export default CalendarTeamMemberVacationRequests
